import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/request';
import type { DocumentResponse } from '@onefootprint/request-types';
import { IdDocImageTypes } from '@onefootprint/types';
import { Button } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import upperFirst from 'lodash/upperFirst';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeaderTitle } from '@/idv/components';
import NavigationHeader from '@/idv/components/layout/components/navigation-header';
import { getLogger, trackAction } from '@/idv/utils';
import IdDocAnimation from '../../../components/id-doc-animation';
import Loading from '../../../components/loading';
import RetryLimitExceeded from '../../../components/retry-limit-exceeded';
import Success from '../../../components/success';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import { useNonIdDocMachine } from '../../components/machine-provider';
import useDocName from '../../hooks/use-doc-name';
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'non-id-doc-desktop-processing'
});
const DesktopProcessing = () => {
  const $ = _c(67);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "document-flow.non-id-doc.pages.desktop-processing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t0);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, send] = useNonIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = postHostedUserDocumentsByIdProcessMutation();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const processDocMutation = useMutation(t1);
  const [mode, setMode] = useState("loading");
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    document,
    authToken,
    id,
    requirement
  } = state.context;
  const {
    kind: documentRequestKind
  } = requirement.config;
  const t2 = useDocName(requirement.config);
  let t3;
  if ($[2] !== t2) {
    t3 = upperFirst(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const documentName = t3;
  let t4;
  if ($[4] !== documentRequestKind) {
    t4 = () => {
      trackAction(`document-processing-${documentRequestKind}:started`);
    };
    $[4] = documentRequestKind;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== documentRequestKind || $[8] !== id || $[9] !== send) {
    t6 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docKind: documentRequestKind
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. doc: ${documentRequestKind}, doc id: ${id}`, undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:retry-limit-exceeded`, rejectionContext);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === IdDocImageTypes.front) {
        logWarn(`Document rejected. doc: ${documentRequestKind}, codes: ${rejectionCodes}`, undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:rejected`, rejectionContext);
        send({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      if (nextSideToCollect) {
        const unexpectedContext = {
          ...rejectionContext,
          nextSide: nextSideToCollect
        };
        logWarn(`Unexpected next side to collect flow. Side: ${nextSideToCollect}`, undefined, unexpectedContext);
        trackAction(`document-processing-${documentRequestKind}:unexpected-next-side`, unexpectedContext);
        return;
      }
      setMode("success");
      trackAction(`document-processing-${documentRequestKind}:completed`, rejectionContext);
    };
    $[7] = documentRequestKind;
    $[8] = id;
    $[9] = send;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleProcessDocSuccess = t6;
  let t7;
  if ($[11] !== send) {
    t7 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      send({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.unknownUploadError
          }]
        }
      });
    };
    $[11] = send;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleError = t7;
  let t8;
  if ($[13] !== getErrorMessage || $[14] !== handleError || $[15] !== id) {
    t8 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[13] = getErrorMessage;
    $[14] = handleError;
    $[15] = id;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleProcessDocError = t8;
  let t9;
  if ($[17] !== documentRequestKind || $[18] !== getErrorCode || $[19] !== getErrorMessage || $[20] !== handleError || $[21] !== id) {
    t9 = err_2 => {
      const errorCode = getErrorCode(err_2);
      const errorMessage = getErrorMessage(err_2);
      if (errorCode === "E109") {
        setMode("success");
        return;
      }
      handleError(err_2);
      logWarn(`Submit failed on phone flow. Document kind: ${documentRequestKind}, upload session id: ${id}. Error: ${errorMessage}`);
    };
    $[17] = documentRequestKind;
    $[18] = getErrorCode;
    $[19] = getErrorMessage;
    $[20] = handleError;
    $[21] = id;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleSubmitDocError = t9;
  let t10;
  if ($[23] !== authToken || $[24] !== handleProcessDocError || $[25] !== handleProcessDocSuccess || $[26] !== id || $[27] !== processDocMutation) {
    t10 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t11) {
        const error = t11;
        handleProcessDocError(error);
      }
    };
    $[23] = authToken;
    $[24] = handleProcessDocError;
    $[25] = handleProcessDocSuccess;
    $[26] = id;
    $[27] = processDocMutation;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const handleSubmitDocSuccess = t10;
  let t11;
  if ($[29] !== authToken || $[30] !== document || $[31] !== handleSubmitDocError || $[32] !== handleSubmitDocSuccess || $[33] !== id || $[34] !== submitDocMutation) {
    t11 = () => {
      if (!document || !authToken || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Desktop web flow - id-doc image could not be processed due to missing requirements. Requirements - document: ${document ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, id: ${id ? "OK" : "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind
      } = document;
      logInfo(`Uploading image with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type} in POST request`);
      submitDocMutation.mutate({
        authToken,
        image: imageFile,
        extraCompress: extraCompressed,
        side: IdDocImageTypes.front,
        id,
        meta: {
          manual: captureKind === "manual",
          isUpload: captureKind === "upload"
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[29] = authToken;
    $[30] = document;
    $[31] = handleSubmitDocError;
    $[32] = handleSubmitDocSuccess;
    $[33] = id;
    $[34] = submitDocMutation;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  useEffect(t11, t12);
  let t13;
  if ($[37] !== send) {
    t13 = () => {
      send({
        type: "retryLimitExceeded"
      });
    };
    $[37] = send;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const handleRetryLimitExceeded = t13;
  let t14;
  if ($[39] !== send) {
    t14 = () => {
      send({
        type: "processingSucceeded"
      });
    };
    $[39] = send;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const handleSuccess = t14;
  if (isMissingRequirements) {
    let t15;
    if ($[41] !== t) {
      t15 = t(_temp2);
      $[41] = t;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    let t16;
    if ($[43] !== t15) {
      t16 = <p className="text-label-1 text-error text-center fp-text">{t15}</p>;
      $[43] = t15;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    return t16;
  }
  if (retryLimitExceeded) {
    let t15;
    if ($[45] !== handleRetryLimitExceeded) {
      t15 = <RetryLimitExceeded onRetryLimitExceeded={handleRetryLimitExceeded} />;
      $[45] = handleRetryLimitExceeded;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    return t15;
  }
  let t15;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <NavigationHeader leftButton={{
      variant: "close",
      confirmClose: true
    }} />;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== documentName) {
    t16 = <HeaderTitle title={documentName} />;
    $[48] = documentName;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== showSlowConnectionMessage || $[51] !== step) {
    t17 = <Loading step={step} showSlowConnectionMessage={showSlowConnectionMessage} />;
    $[50] = showSlowConnectionMessage;
    $[51] = step;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Success />;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== mode || $[55] !== t17) {
    t19 = <div className="flex flex-col justify-center items-center bg-secondary border border-dashed border-primary rounded min-h-[280px]"><IdDocAnimation loadingComponent={t17} successComponent={t18} mode={mode} hasNextSide={false} /></div>;
    $[54] = mode;
    $[55] = t17;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const t20 = mode === "loading";
  let t21;
  if ($[57] !== t) {
    t21 = t(_temp3);
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== handleSuccess || $[60] !== t20 || $[61] !== t21) {
    t22 = <Button fullWidth={true} disabled={t20} onClick={handleSuccess} size="large">{t21}</Button>;
    $[59] = handleSuccess;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t16 || $[64] !== t19 || $[65] !== t22) {
    t23 = <div className="flex flex-col gap-6 max-md:px-2">{t15}{t16}{t19}{t22}</div>;
    $[63] = t16;
    $[64] = t19;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
};
export default DesktopProcessing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}
function _temp2($) {
  return $["missing-requirement-error"];
}
function _temp3($_0) {
  return $_0.continue;
}